/**
 * Helper pour générer des tickets de vente
 */

import { PrintJob, PrintLine } from './PrinterAdapter';
import type { Sale, SaleLine } from '../../types/pos.types';
import { formatPrice as formatPriceBase } from '../../utils/pos.utils';
import { couperAuMot } from '../../utils/commentairesPrecommande';

/**
 * Normaliser les caractères accentués pour l'impression EPSON
 * Remplace les accents par leurs équivalents ASCII pour éviter les problèmes d'encodage PC858
 */
function normalizeForPrint(text: string): string {
  const accentsMap: Record<string, string> = {
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o',
    'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
    'ç': 'c', 'ñ': 'n',
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
    'Ç': 'C', 'Ñ': 'N',
    // Ligatures : absentes du jeu PC858, le bridge imprimait « Bouillon de b?uf »
    // (11 produits actifs, 36 lignes de ticket en 30 jours — 23/10/2026).
    'œ': 'oe', 'Œ': 'OE', 'æ': 'ae', 'Æ': 'AE',
    '€': 'EUR',
  };

  return text.split('').map(char => accentsMap[char] || char).join('');
}

/**
 * Format prix pour impression - utilise "EUR" au lieu du symbole €
 * Le symbole € pose des problèmes d'encodage PC858 sur certaines imprimantes EPSON
 */
function formatPrice(cents: number): string {
  return normalizeForPrint(formatPriceBase(cents));
}

export interface ReceiptOptions {
  storeName?: string;
  storeAddress?: string;
  storePhone?: string;
  storeTaxNumber?: string;
  /** IBAN des Folies — imprimé uniquement sur les tickets payés par virement. */
  storeIban?: string;
  showBarcode?: boolean;
  showQRCode?: boolean;
  footer?: string;
  /**
   * DLC à imprimer sous la ligne, par product_id (date ISO). Construite au moment
   * de l'impression depuis le catalogue POS local : produits « DLC sur ticket »
   * (œufs en vrac — obligation d'information) ayant une DLC de lot en rayon.
   */
  dlcByProductId?: Record<string, string>;
}

/** ISO → JJ/MM/AAAA pour l'impression. */
function formatDlcForPrint(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
}

/**
 * Construit la carte product_id → DLC (ISO) depuis le catalogue POS local.
 * À passer en `dlcByProductId` aux impressions de ticket. Fonctionne hors-ligne
 * (le catalogue est en cache local).
 */
export function buildDlcMap(
  products: Array<{ id: string; print_dlc_on_ticket?: boolean; display_expiry_date?: string | null }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const p of products) {
    if (p.print_dlc_on_ticket && p.display_expiry_date) {
      map[p.id] = p.display_expiry_date;
    }
  }
  return map;
}

/**
 * Générer un ticket de vente
 */
export function generateSaleReceipt(
  sale: Sale,
  options: ReceiptOptions = {}
): PrintJob {
  const lines: PrintLine[] = [];

  // Header - Format foliespos
  // 1. Nom du magasin
  lines.push({
    type: 'text',
    content: normalizeForPrint(options.storeName || 'Les Folies Maraicheres SRL'),
    align: 'center',
    bold: true,
  });

  // 2. Téléphone (juste sous le titre)
  const phone = options.storePhone || '0478/51.68.22';
  lines.push({
    type: 'text',
    content: `Tel.: ${phone}`,
    align: 'center',
  });

  // 3. Adresse
  if (options.storeAddress) {
    lines.push({
      type: 'text',
      content: normalizeForPrint(options.storeAddress),
      align: 'center',
    });
  }

  lines.push({ type: 'separator' });

  // Date/Heure (format français comme foliespos)
  const date = new Date(sale.created_at);
  const dateStr = date.toLocaleString('fr-BE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  lines.push({
    type: 'text',
    content: dateStr,
  });

  // Vendeur (format foliespos)
  const vendorName = sale.vendor?.name || 'Non specifie';
  lines.push({
    type: 'text',
    content: normalizeForPrint(`Vendeur : ${vendorName}`),
  });

  lines.push({ type: 'separator' });

  // Articles (format foliespos: nom sur une ligne, puis "qté x PU = Total")
  if (sale.lines) {
    for (const line of sale.lines as SaleLine[]) {
      // Nom produit (normalisé pour impression)
      let productName = line.product_name;
      if (line.is_deposit) productName = 'DEP ' + productName;
      if (line.is_return) productName += ' (retour)';

      lines.push({
        type: 'text',
        content: normalizeForPrint(productName),
      });

      // Format foliespos: "qté x PU = Total" aligné à droite
      // Convertir quantity en number (peut être un Decimal de Prisma ou une string)
      const quantity = Number(line.quantity);
      const qty = quantity % 1 !== 0 ? quantity.toFixed(3) : String(quantity);
      const unitPrice = formatPrice(line.unit_price_cents);
      const total = formatPrice(Math.round(quantity * line.unit_price_cents));

      lines.push({
        type: 'text',
        content: `${qty} x ${unitPrice} = ${total}`,
      });

      // Remise anti-gaspi par lot : le client voit ce qu'il a économisé, et le
      // prix normal auquel l'article se vend d'habitude.
      const origine = Number((line as any).original_price_cents);
      if (origine > line.unit_price_cents) {
        const taux = Number((line as any).lot_promo_percent)
          || Math.round((1 - line.unit_price_cents / origine) * 100);
        lines.push({
          type: 'text',
          content: normalizeForPrint(`  Anti-gaspi -${taux} % (prix normal ${formatPrice(origine)})`),
        });
      }

      // Date sous la ligne — produits marqués « DLC sur ticket » (pas les consignes).
      // Formule DDM (« de préférence ») et non DLC : les œufs restent consommables
      // après la date — « à consommer avant le » serait faux et ferait jeter.
      const dlcIso = !line.is_deposit && line.product_id
        ? options.dlcByProductId?.[line.product_id]
        : undefined;
      if (dlcIso) {
        const dlcTxt = formatDlcForPrint(dlcIso);
        if (dlcTxt) {
          lines.push({
            type: 'text',
            content: normalizeForPrint(`  De preference avant le ${dlcTxt}`),
          });
        }
      }
    }
  }

  lines.push({ type: 'separator' });

  // Totaux (format foliespos avec colonnes)
  const totalTTC = formatPrice(sale.total_ttc_cents || sale.total_cents || 0);

  lines.push({
    type: 'text',
    content: `TOTAL = ${totalTTC}`,
    bold: true,
    fontSize: 'large',
  });

  // Méthode de paiement
  const methodLabels: Record<string, string> = {
    cash: 'Especes',
    bancontact: 'Bancontact',
    meal_voucher: 'Cheques repas',
    wero: 'Wero / QR',
    virement: 'Virement',
    card: 'Carte', // Legacy
    wallet: 'Wallet',
    online: 'Paye en ligne',
    mixed: 'Mixte',
  };
  const paymentLabel = methodLabels[sale.payment_method] || sale.payment_method;

  lines.push({
    type: 'text',
    content: `PAIEMENTS`,
  });

  // Détail paiement mixte : ventiler chaque méthode
  if (sale.payment_method === 'mixed' && sale.payment_metadata) {
    const mixedLabels: Record<string, string> = {
      cash_cents: 'Especes',
      bancontact_cents: 'Bancontact',
      meal_voucher_cents: 'Cheques repas',
      wero_cents: 'Wero / QR',
      wallet_cents: 'Wallet',
    };

    for (const [key, label] of Object.entries(mixedLabels)) {
      const amount = sale.payment_metadata[key];
      if (amount && amount > 0) {
        lines.push({
          type: 'text',
          content: `  ${label} = ${formatPrice(amount)}`,
        });
      }
    }

    lines.push({
      type: 'text',
      content: `TOTAL = ${totalTTC}`,
      bold: true,
    });

    // Rendu monnaie sur la part espèces
    if (sale.change_cents && sale.change_cents > 0) {
      const change = formatPrice(sale.change_cents);
      lines.push({
        type: 'text',
        content: `Rendu = ${change}`,
        bold: true,
      });
    }
  } else {
    lines.push({
      type: 'text',
      content: `${paymentLabel} = ${totalTTC}`,
    });
  }

  // Afficher montant reçu et rendu pour espèces pures
  if (sale.payment_method === 'cash' && sale.amount_paid_cents) {
    const amountPaid = formatPrice(sale.amount_paid_cents);
    lines.push({
      type: 'text',
      content: `Recu = ${amountPaid}`,
    });

    if (sale.change_cents && sale.change_cents > 0) {
      const change = formatPrice(sale.change_cents);
      lines.push({
        type: 'text',
        content: `Rendu = ${change}`,
        bold: true,
      });
    }
  }

  // Virement : le client doit repartir avec de quoi payer. On imprime le compte
  // des Folies + une communication (le numéro de ticket) pour le rapprochement.
  // Rien n'est imprimé si l'IBAN n'est pas renseigné dans les réglages magasin —
  // mieux vaut une absence qu'un numéro de compte faux.
  if (sale.payment_method === 'virement' && options.storeIban) {
    lines.push({ type: 'feed', feeds: 1 });
    lines.push({ type: 'text', content: 'A VIRER SUR LE COMPTE :', bold: true });
    lines.push({ type: 'text', content: options.storeIban, bold: true });
    if (options.storeName) {
      lines.push({ type: 'text', content: options.storeName });
    }
    lines.push({ type: 'text', content: `Communication : ${sale.sale_number}` });
  }

  lines.push({ type: 'feed', feeds: 1 });

  // Détail TVA (format foliespos)
  const totalTVA = formatPrice(
    sale.total_tva_cents ||
    sale.tax_total_cents ||
    ((sale.total_ttc_cents || sale.total_cents || 0) - (sale.total_ht_cents || 0))
  );

  lines.push({
    type: 'text',
    content: `TVA = ${totalTVA}`,
    fontSize: 'small',
  });

  // Mention facture demandée (si applicable)
  if (sale.invoice_requested) {
    lines.push({ type: 'feed', feeds: 1 });
    lines.push({
      type: 'text',
      content: '*** FACTURE DEMANDEE ***',
      align: 'center',
      bold: true,
    });
    // Le mode de paiement est déjà ventilé plus haut, sous PAIEMENTS. On le
    // répète ICI parce que ce bloc est celui qu'on lit pour établir la facture :
    // la facture doit porter la façon dont le client a réglé, et aller la
    // rechercher vingt lignes plus haut sur un ticket plié est une erreur qui
    // attend son tour. Répétition voulue, pas un oubli.
    lines.push({
      type: 'text',
      content: normalizeForPrint(`Paiement : ${paymentLabel}`),
      align: 'center',
      bold: true,
    });
    lines.push({
      type: 'text',
      content: 'A retirer au comptoir',
      align: 'center',
      fontSize: 'small',
    });
  }

  // Bons cadeaux vendus : leur code n'est imprimé QU'ICI, une seule fois. Il n'est
  // jamais réaffiché en clair ailleurs — un bon vivant est un titre au porteur, et
  // la liste du tableau de bord ne montre que ses quatre derniers caractères. Si le
  // ticket est perdu avant d'être recopié, le bon doit être annulé et refait.
  const bons = (sale as any).gift_vouchers as
    | Array<{ code_formate: string; amount_cents: number }>
    | undefined;
  if (bons && bons.length > 0) {
    for (const bon of bons) {
      lines.push({ type: 'feed', feeds: 1 });
      lines.push({ type: 'separator' });
      lines.push({
        type: 'text',
        content: '*** BON CADEAU ***',
        align: 'center',
        bold: true,
      });
      lines.push({
        type: 'text',
        content: `${(bon.amount_cents / 100).toFixed(2)} EUR`,
        align: 'center',
        bold: true,
      });
      lines.push({ type: 'feed', feeds: 1 });
      lines.push({
        type: 'text',
        content: bon.code_formate,
        align: 'center',
        bold: true,
        fontSize: 'large',
      });
      lines.push({ type: 'feed', feeds: 1 });
      lines.push({
        type: 'text',
        content: 'A saisir dans « Mes FOLIES » sur foliesboutik.be,',
        align: 'center',
        fontSize: 'small',
      });
      lines.push({
        type: 'text',
        content: 'ou a presenter en magasin. Valable 1 an.',
        align: 'center',
        fontSize: 'small',
      });
      lines.push({
        type: 'text',
        content: 'Utilisable une seule fois. Conservez ce code.',
        align: 'center',
        fontSize: 'small',
      });
    }
  }

  lines.push({ type: 'separator' });

  // Mention BIO obligatoire (certification)
  lines.push({
    type: 'text',
    content: 'CONTROLE BE-BIO-01',
    align: 'center',
    bold: true,
  });

  // Numéro de TVA (sous CONTROLE BE-BIO-01)
  const taxNumber = options.storeTaxNumber || '0715954426';
  lines.push({
    type: 'text',
    content: `TVA: ${taxNumber}`,
    align: 'center',
  });

  lines.push({ type: 'separator' });

  // Code-barres du numéro de ticket (optionnel)
  if (options.showBarcode) {
    lines.push({ type: 'feed', feeds: 1 });
    lines.push({
      type: 'barcode',
      content: sale.sale_number.replace(/[^0-9]/g, ''), // Garder que chiffres
      align: 'center',
    });
  }

  // QR Code (optionnel) - pourrait contenir lien vers facture digitale
  if (options.showQRCode) {
    lines.push({ type: 'feed', feeds: 1 });
    // QR code avec lien web (scannable par smartphone du client)
    lines.push({
      type: 'qrcode',
      content: `https://foliesboutik.be/sales/${sale.id}`,
      align: 'center',
      size: 4,
    });
  }

  // Numéro de vente — TOUJOURS imprimé.
  // Il ne l'était que dans le bloc QR ci-dessus : sans QR, le ticket remis au
  // client ne portait aucune référence. C'est pourtant elle qui permet de
  // retrouver la vente lors d'un retour, d'une contestation ou d'une demande
  // de facture — le numéro est d'ailleurs la communication d'un paiement par
  // virement. Absent de tous les tickets depuis le 28/05/2026.
  lines.push({ type: 'feed', feeds: 1 });
  lines.push({
    type: 'text',
    content: sale.sale_number,
    align: 'center',
    fontSize: 'small',
  });

  // Footer (format foliespos)
  if (options.footer) {
    lines.push({
      type: 'text',
      content: normalizeForPrint(options.footer),
      align: 'center',
    });
  } else {
    lines.push({
      type: 'text',
      content: 'A bientot!',
      align: 'center',
    });
  }

  // Espace avant coupe
  lines.push({ type: 'feed', feeds: 3 });

  return {
    lines,
    metadata: {
      saleNumber: sale.sale_number,
      vendorName: sale.vendor?.name || undefined,
      timestamp: date,
    },
  };
}

/**
 * Étiquette de précommande sur ticket de caisse EPSON.
 *
 * Remplace l'étiquette Zebra : ce que l'étiquette doit faire, c'est identifier
 * un sac d'un coup d'œil et se laisser scanner au comptoir. Le nom du client
 * répond au premier besoin, le code-barres au second — la liste des articles,
 * elle, est sur la feuille de préparation. Les CONSIGNES, si : « coupé », la
 * remarque du vendeur, ce que le client a écrit en commandant en ligne
 * (`commentairesPrecommande`) — c'est ce qu'on vérifie en fermant le sac
 * (demande de Philippe, 23/10/2026).
 *
 * Le code-barres encode le NUMÉRO DE PRÉCOMMANDE tel quel (« PRE-2026-031 ») :
 * le bridge imprime en CODE128 jeu B, qui accepte lettres et tirets, et la
 * caisse route déjà tout ce qui commence par « PRE » vers la remise de
 * précommande. Rien à ajouter côté POS pour que le scan fonctionne.
 */
export function generatePreorderLabelTicket(data: {
  preorderNumber: string;
  customerName: string;
  /** Date de RETRAIT (et non de fabrication) : c'est le jour où le sac sort. */
  pickupDate?: string | null;
  /** Préparé ce jour-ci mais retiré plus tard → le sac part en réserve. */
  aMettreDeCote?: boolean;
  /** Lignes de `commentairesPrecommande` : coupé, remarques, note du client. */
  commentaires?: string[];
}): PrintJob {
  const lines: PrintLine[] = [];
  const nom = normalizeForPrint((data.customerName || 'Client').trim().toUpperCase());

  lines.push({ type: 'feed', feeds: 1 });

  // La double largeur divise par deux le nombre de caractères par ligne : au-delà
  // d'une vingtaine de signes, un nom long déborderait. On garde alors la double
  // hauteur seule, qui reste lisible à bout de bras sans rien tronquer.
  lines.push({
    type: 'text',
    content: nom,
    align: 'center',
    bold: true,
    fontSize: nom.length <= 20 ? 'xlarge' : 'large',
  });

  lines.push({ type: 'text', content: data.preorderNumber, align: 'center' });

  if (data.pickupDate) {
    const d = new Date(String(data.pickupDate).slice(0, 10) + 'T12:00:00');
    if (!Number.isNaN(d.getTime())) {
      lines.push({ type: 'feed', feeds: 1 });
      lines.push({
        type: 'text',
        content: normalizeForPrint(
          'Retrait ' +
            d.toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' }),
        ),
        align: 'center',
      });
    }
  }

  if (data.aMettreDeCote) {
    lines.push({ type: 'text', content: 'A METTRE DE COTE', align: 'center', bold: true });
  }

  // Les consignes entre deux traits. Coupées au mot ICI : le bridge envoie la
  // ligne telle quelle et l'imprimante la tranchait au milieu d'un mot.
  const commentaires = (data.commentaires || []).filter((c) => c.trim());
  if (commentaires.length > 0) {
    lines.push({ type: 'separator' });
    for (const c of commentaires) {
      for (const ligne of couperAuMot(normalizeForPrint(c), 40)) {
        lines.push({ type: 'text', content: ligne, bold: true });
      }
    }
    lines.push({ type: 'separator' });
  }

  lines.push({ type: 'feed', feeds: 1 });
  lines.push({
    type: 'barcode',
    content: barcodeForOrderNumber(data.preorderNumber),
    align: 'center',
  });
  lines.push({ type: 'feed', feeds: 2 });

  return {
    lines,
    metadata: { saleNumber: data.preorderNumber, timestamp: new Date() },
  };
}

/**
 * Contenu à encoder dans un code-barres : lettres et chiffres UNIQUEMENT.
 *
 * Une douchette est un clavier — elle « tape » le code selon SA disposition. Les
 * caisses sont en AZERTY belge et la douchette en AZERTY français, or le tiret
 * n'y est pas sur la même touche : « PRE-2026-014 » arrivait en
 * « PRE§2026§014 » et aucune recherche ne le retrouvait. Lettres et chiffres
 * occupent en revanche les mêmes touches dans les deux dispositions.
 *
 * On encode donc « PRE2026014 » ; la caisse reconstitue le numéro complet.
 */
export function barcodeForOrderNumber(orderNumber: string): string {
  return (orderNumber || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** Ligne de commande telle que l'écran de préparation la manipule. */
export interface PickupTicketLine {
  product_name: string;
  /** Sert à retrouver la date à imprimer dans `dlcByProductId`. */
  product_id?: string;
  quantity: number;
  unit_price_cents: number;
  total_price_cents?: number;
  /** 'pieces' quand un article au poids est vendu à la pièce (pain, fromage). */
  quantity_unit?: string;
  product?: { is_weighted?: boolean; is_deposit?: boolean | null };
  /** 'consigne' = ligne de vidange ajoutée par le serveur (bouteilles, bocaux). */
  added_by?: string;
  /** Offre anti-gaspi d'un lot choisie en ligne. */
  lot_promo_percent?: number | null;
  lot_expiry_date?: string | null;
  /** « coupé » et remarque libre : ce qui distingue deux pains identiques. */
  prep_options?: string[];
  note?: string;
  /** Ce que le client avait commandé, quand la préparation l'a modifié */
  ordered_quantity?: number;
  /**
   * Unité de la quantité COMMANDÉE, figée à la création — distincte de
   * `quantity_unit`, qui décrit ce qui a été PRÉPARÉ. Peser 5 citrons les
   * passe en grammes : sans elle, on ne sait plus que le client en avait
   * demandé 5 pièces. NULL est le cas normal d'un article à la pièce.
   */
  ordered_quantity_unit?: string | null;
  ordered_total_price_cents?: number;
}

/**
 * Quantité COMMANDÉE d'une ligne, formatée avec SON unité (grammes ou pièces),
 * jamais celle de la préparation — voir l'incident Bouvier plus bas. Repli en OU
 * sur la nature du produit quand l'unité figée manque, jamais avec `??`.
 */
function quantiteCommandeeTexte(l: PickupTicketLine): string | null {
  if (l.ordered_quantity === undefined || l.ordered_quantity === null) return null;
  const q = Number(l.ordered_quantity);
  const auPoids = l.quantity_unit !== 'pieces' && l.product?.is_weighted;
  const enGrammes =
    l.ordered_quantity_unit === 'grams' ? true : l.ordered_quantity_unit === 'pieces' ? false : auPoids;
  return enGrammes ? (q >= 1000 ? `${(q / 1000).toFixed(q % 1000 === 0 ? 0 : 2)} kg` : `${q} g`) : `x${q}`;
}

/** Montant qu'avait la ligne à la commande (ce qui est rendu si elle manque). */
function montantCommande(l: PickupTicketLine): number | null {
  if (l.ordered_total_price_cents !== undefined && l.ordered_total_price_cents !== null) return l.ordered_total_price_cents;
  if (l.ordered_quantity === undefined || l.ordered_quantity === null) return null;
  return Math.round(Number(l.ordered_quantity) * l.unit_price_cents);
}

/** Une ligne est manquante quand la préparation l'a ramenée à zéro. */
export function estManquante(l: PickupTicketLine): boolean {
  // Une consigne suit ses bouteilles : si tous les jus manquent, elle tombe à
  // zéro avec eux — c'est le jus qui est « non fourni », pas la vidange.
  if (estConsigne(l)) return false;
  return Number(l.quantity) <= 0;
}

function estConsigne(l: PickupTicketLine): boolean {
  return l.added_by === 'consigne' || l.product?.is_deposit === true;
}

/**
 * Ticket qui accompagne le sac d'une commande préparée.
 *
 * Existait déjà côté serveur, mais l'API tourne sur le VPS et lançait `lpr` sur
 * une machine sans imprimante : le ticket ne sortait jamais, et l'écran se
 * contentait d'un avertissement discret. On le construit donc ici, pour
 * l'imprimante réellement branchée au poste.
 *
 * Le code-barres encode le numéro tel quel : la caisse route « PRE… » vers la
 * remise de précommande et « CMD… » vers le retrait de commande.
 */
export interface ColisTicket {
  zone: 'caisse' | 'frigo' | 'pain' | 'autre';
  commentaire?: string;
}
const ZONE_TICKET: Record<ColisTicket['zone'], string> = { caisse: 'CAISSE', frigo: 'FRIGO', pain: 'PAIN', autre: 'AUTRE' };

/**
 * « N COLIS » en très grand, puis un colis par ligne avec sa zone et son
 * commentaire (« 2. FRIGO - pack de lait ») — coupé au mot à 40 caractères.
 */
function lignesColis(lines: PrintLine[], liste: ColisTicket[], numeros?: number[]) {
  const n = liste.length;
  lines.push({ type: 'text', content: `${n} COLIS`, align: 'center', bold: true, fontSize: 'xlarge' });
  liste.forEach((c, i) => {
    const texte = `${numeros?.[i] ?? i + 1}. ${ZONE_TICKET[c.zone]}${c.commentaire ? ` - ${c.commentaire}` : ''}`;
    for (const morceau of couperAuMot(normalizeForPrint(texte), 40)) {
      lines.push({ type: 'text', content: morceau, bold: true });
    }
  });
}

/**
 * Ticket FRIGO (26/10/2026) : posé sur le(s) colis du frigo, pour qu'on ne remette
 * pas le sac sec en oubliant le froid. Le nom en grand, le total des colis, les
 * colis frigo avec leurs commentaires, les articles frais, le code-barres comptoir.
 */
export function generateFrigoTicket(order: {
  order_number: string;
  customer_name: string;
  delivery_option?: string | null;
  colis_detail: ColisTicket[];
  lines: Array<PickupTicketLine & { product?: { froid?: boolean } }>;
  storeName?: string;
}): PrintJob | null {
  const numeros = order.colis_detail.map((_, i) => i + 1);
  const frigo = order.colis_detail.map((c, i) => ({ c, i })).filter(({ c }) => c.zone === 'frigo');
  if (!frigo.length) return null;
  const lines: PrintLine[] = [];
  lines.push({ type: 'text', content: 'FRIGO', align: 'center', bold: true, fontSize: 'xlarge' });
  lines.push({ type: 'text', content: order.order_number, align: 'center', bold: true, fontSize: 'large' });
  lines.push({ type: 'separator' });
  const nom = normalizeForPrint((order.customer_name || 'Client').trim());
  lines.push({ type: 'text', content: nom, bold: true, fontSize: nom.length <= 20 ? 'xlarge' : 'large' });
  lines.push({
    type: 'text',
    content: order.delivery_option === 'delivery' ? 'LIVRAISON A DOMICILE' : 'RETRAIT EN MAGASIN',
    bold: true,
  });
  const total = order.colis_detail.length;
  lines.push({ type: 'text', content: `${total} colis au total, dont ${frigo.length} au frigo`, bold: true, fontSize: 'large' });
  lines.push({ type: 'separator' });
  lignesColis(lines, frigo.map(({ c }) => c), frigo.map(({ i }) => numeros[i]));
  const frais = order.lines.filter((l) => l.product?.froid === true && Number(l.quantity) > 0 && !estConsigne(l));
  if (frais.length) {
    lines.push({ type: 'separator' });
    lines.push({ type: 'text', content: 'Articles frais :', bold: true });
    for (const l of frais) {
      const details = (l.prep_options ?? []).join(', ');
      for (const morceau of couperAuMot(normalizeForPrint(`- ${l.product_name}${details ? ` (${details})` : ''}`), 40)) {
        lines.push({ type: 'text', content: morceau });
      }
    }
  }
  lines.push({ type: 'feed', feeds: 1 });
  lines.push({ type: 'barcode', content: barcodeForOrderNumber(order.order_number), align: 'center' });
  lines.push({ type: 'feed', feeds: 1 });
  lines.push({ type: 'text', content: normalizeForPrint(order.storeName || 'Les Folies Maraicheres'), align: 'center' });
  lines.push({ type: 'feed', feeds: 3 });
  return { lines, metadata: { saleNumber: `${order.order_number}-FRIGO`, timestamp: new Date() } };
}

export function generatePickupTicket(order: {
  order_number: string;
  customer_name: string;
  customer_phone?: string | null;
  /** 'delivery' = à livrer (adresse imprimée) ; sinon retrait en magasin. */
  delivery_option?: string | null;
  /**
   * 'paid' = déjà réglée. C'est LUI qui dit payé / à payer (22/10/2026) : une
   * commande livrée peut être payée en ligne ou « à régler » à la porte. Sans
   * lui (ancien appel), repli sur `prepaid_pickup` = déjà réglée.
   */
  payment_status?: string | null;
  /** Livraison en plusieurs colis : annoncé au client. */
  colis?: number | null;
  /**
   * Colis notés à « Commande prête » (26/10/2026) : « N COLIS » en grand et la liste,
   * pour savoir QUOI remettre, au comptoir comme à la porte.
   */
  colis_detail?: ColisTicket[] | null;
  delivery_date?: string | null;
  delivery_slot?: string | null;
  /** Commande À LIVRER : l'adresse est imprimée (le ticket voyage avec le sac). */
  delivery_address?: { street: string; postal_code: string; city: string } | null;
  total_cents: number;
  /** Complément de pesée à régler au retrait (poids supérieurs) */
  balance_due_cents?: number;
  /** Déjà remboursé sur les FOLIES (poids inférieurs, articles manquants) */
  refunded_cents?: number;
  lines: PickupTicketLine[];
  preparedBy?: string | null;
  storeName?: string;
  /**
   * product_id → date (ISO), pour les seuls produits cochés « DLC sur ticket ».
   * Même carte et même formule qu'un ticket de caisse : un client qui achète des
   * œufs au comptoir et un client qui vient chercher sa commande doivent lire la
   * même chose. Le drapeau n'était tout simplement pas branché ici.
   */
  dlcByProductId?: Record<string, string>;
  /** Bacs Folies vides que le client rapporte : échangés un pour un au retrait. */
  bacs_rapportes?: number;
  /** Adresse publique de la commande : QR destiné au CLIENT (20/10/2026). */
  lienClient?: string | null;
}): PrintJob {
  const lines: PrintLine[] = [];
  // Commande à livrer : le même ticket part dans le sac, avec l'adresse du
  // client (demande de Philippe, 13/10/2026) — « retrait en magasin » y était faux.
  const aLivrer = order.delivery_option === 'delivery';

  lines.push({ type: 'text', content: aLivrer ? 'LIVRAISON A DOMICILE' : 'RETRAIT EN MAGASIN', align: 'center', bold: true });
  lines.push({
    type: 'text',
    content: order.order_number,
    align: 'center',
    bold: true,
    fontSize: 'xlarge',
  });
  const colis = Math.max(1, Math.round(Number(order.colis) || 1));
  if (order.colis_detail?.length) {
    lignesColis(lines, order.colis_detail);
  } else if (aLivrer && colis > 1) {
    lines.push({ type: 'text', content: `Votre livraison : ${colis} colis`, align: 'center', bold: true });
  }
  lines.push({ type: 'separator' });

  const nom = normalizeForPrint((order.customer_name || 'Client').trim());
  lines.push({
    type: 'text',
    content: nom,
    bold: true,
    fontSize: nom.length <= 20 ? 'xlarge' : 'large',
  });
  if (aLivrer && order.delivery_address) {
    const a = order.delivery_address;
    lines.push({ type: 'text', content: normalizeForPrint(a.street || ''), bold: true, fontSize: 'large' });
    lines.push({ type: 'text', content: normalizeForPrint(`${a.postal_code || ''} ${a.city || ''}`.trim()), bold: true, fontSize: 'large' });
  } else if (aLivrer) {
    lines.push({ type: 'text', content: 'ADRESSE MANQUANTE - voir la commande', bold: true });
  }
  if (order.customer_phone) {
    lines.push({ type: 'text', content: normalizeForPrint(`Tel ${order.customer_phone}`) });
  }

  if (order.delivery_date) {
    const d = new Date(String(order.delivery_date).slice(0, 10) + 'T12:00:00');
    if (!Number.isNaN(d.getTime())) {
      lines.push({
        type: 'text',
        content: normalizeForPrint(
          (aLivrer ? 'Livraison ' : 'Retrait ') +
            d.toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' }) +
            (order.delivery_slot ? ` - ${order.delivery_slot}` : ''),
        ),
        bold: true,
      });
    }
  }

  // Le ticket voyage avec la commande jusqu'au comptoir : celui qui la remet
  // doit savoir combien de bacs vides reprendre (échange un pour un).
  if ((order.bacs_rapportes ?? 0) > 0) {
    const n = order.bacs_rapportes!;
    lines.push({ type: 'text', content: `BACS FOLIES A ECHANGER : ${n}`, bold: true, fontSize: 'large' });
    lines.push({ type: 'text', content: `Reprendre ${n} bac${n > 1 ? 's' : ''} vide${n > 1 ? 's' : ''} propre${n > 1 ? 's' : ''}` });
    // Les clients croyaient être remboursés de toute caisse rapportée (17/10/2026).
    lines.push({ type: 'text', content: 'Bacs bleus uniquement - echange, sans remboursement' });
  }

  lines.push({ type: 'separator' });

  // Les articles NON FOURNIS, en tête et regroupés. Ils étaient déjà signalés,
  // mais chacun à sa place dans la liste : sur une commande de 41 lignes, une
  // ligne « MANQUANT » au milieu passait inaperçue — pour le client qui vérifie
  // son sac comme pour celui qui le remet (demande de Philippe, 25/09/2026).
  // La mention reste AUSSI dans la liste, à sa place.
  const manquants = order.lines.filter(estManquante);
  if (manquants.length > 0) {
    lines.push({
      type: 'text',
      content: `ARTICLES NON FOURNIS (${manquants.length})`,
      bold: true,
      fontSize: 'large',
    });
    for (const l of manquants) {
      const q = quantiteCommandeeTexte(l);
      const m = montantCommande(l);
      lines.push({ type: 'text', content: normalizeForPrint(`- ${l.product_name}`) });
      if (q || m) {
        lines.push({
          type: 'text',
          content: `  ${[q, m ? formatPrice(m) : null].filter(Boolean).join(' = ')}`,
        });
      }
    }
    lines.push({ type: 'text', content: 'Rembourses sur vos FOLIES', bold: true });
    lines.push({ type: 'separator' });
  }

  // Toutes les lignes, sans troncature : le client vérifie son sac avec ce
  // ticket en main. La version serveur s'arrêtait à 5 articles et résumait le
  // reste — inutilisable pour une commande de pain qui en compte huit.
  // Une consigne tombée à zéro (plus aucune bouteille dans le sac) n'a rien à
  // dire au client.
  for (const l of order.lines.filter((x) => !(estConsigne(x) && Number(x.quantity) <= 0))) {
    const qte = Number(l.quantity);
    // Unité dite d'abord (« grams »), nature du produit ensuite : une fiche au
    // kilo sans le drapeau `is_weighted` s'imprimerait « x1000 ».
    const auPoids = l.quantity_unit === 'grams' || (l.quantity_unit !== 'pieces' && l.product?.is_weighted);
    const qteTxt = auPoids
      ? qte >= 1000
        ? `${(qte / 1000).toFixed(qte % 1000 === 0 ? 0 : 2)} kg`
        : `${qte} g`
      : `x${qte}`;

    const details = [...(l.prep_options ?? []), ...(l.note ? [l.note] : [])].join(', ');
    lines.push({
      type: 'text',
      content: normalizeForPrint(l.product_name + (details ? ` (${details})` : '')),
    });
    if (l.lot_promo_percent) {
      const d = l.lot_expiry_date ? String(l.lot_expiry_date).slice(0, 10).split('-').reverse().join('/') : null;
      lines.push({
        type: 'text',
        content: normalizeForPrint(`  Anti-gaspi -${l.lot_promo_percent} %${d ? ` (date du lot ${d})` : ''}`),
      });
    }
    // La quantité COMMANDÉE se formate avec SON unité, pas avec celle de la
    // préparation. Signalé le 17/09/2026 (commande Bouvier) : 5 citrons pesés
    // à 860 g s'imprimaient « commande 5 g = 4,50 € » — le client lit une
    // demande qu'il n'a jamais faite. Repli en OU sur la nature du produit
    // quand l'unité figée manque (cas normal d'un article à la pièce), jamais
    // avec `??` : `is_weighted` vaut false par défaut.
    const auPoidsCommande =
      l.ordered_quantity_unit === 'grams'
        ? true
        : l.ordered_quantity_unit === 'pieces'
          ? false
          : auPoids;
    const fmtQteAvec = (q: number, enGrammes: boolean | undefined) =>
      enGrammes ? (q >= 1000 ? `${(q / 1000).toFixed(q % 1000 === 0 ? 0 : 2)} kg` : `${q} g`) : `x${q}`;
    const fmtQte = (q: number) => fmtQteAvec(q, auPoidsCommande);
    const commande = l.ordered_quantity !== undefined && l.ordered_quantity !== null ? Number(l.ordered_quantity) : null;
    const modifie = commande !== null && Math.abs(commande - qte) > 0.0005;
    if (qte <= 0) {
      // Article absent : ramené à zéro à la préparation, rembourse en FOLIES.
      const orderedTotal = l.ordered_total_price_cents ?? (commande !== null ? Math.round(commande * l.unit_price_cents) : null);
      lines.push({
        type: 'text',
        content: `  MANQUANT${orderedTotal ? ` (${fmtQte(commande!)} = ${formatPrice(orderedTotal)})` : ''} - rembourse sur vos FOLIES`,
      });
      continue;
    }
    const total = l.total_price_cents ?? Math.round(qte * l.unit_price_cents);
    if (modifie) {
      // Le client voit ce qu'il avait commandé et ce qu'il emporte.
      const orderedTotal = l.ordered_total_price_cents ?? Math.round(commande! * l.unit_price_cents);
      const ecart = total - orderedTotal;
      lines.push({ type: 'text', content: `  commande ${fmtQte(commande!)} = ${formatPrice(orderedTotal)}` });
      lines.push({
        type: 'text',
        content: `  prepare ${qteTxt} = ${formatPrice(total)} (${ecart < 0 ? '-' : '+'}${formatPrice(Math.abs(ecart))})`,
      });
    } else {
      lines.push({ type: 'text', content: `  ${qteTxt} = ${formatPrice(total)}` });
    }

    // Formule DDM (« de préférence ») et non DLC : les œufs restent consommables
    // après la date — « à consommer avant le » serait faux et ferait jeter. Même
    // texte que le ticket de caisse, volontairement.
    const dlcIso = l.product_id ? order.dlcByProductId?.[l.product_id] : undefined;
    if (dlcIso) {
      const dlcTxt = formatDlcForPrint(dlcIso);
      if (dlcTxt) {
        lines.push({
          type: 'text',
          content: normalizeForPrint(`  De preference avant le ${dlcTxt}`),
        });
      }
    }
  }

  lines.push({ type: 'separator' });

  // Payé ou non : le STATUT de paiement fait foi, pas le mode de remise. Une
  // livraison payée en ligne s'imprimait « TOTAL A PAYER — Paiement au retrait »,
  // et une livraison « à régler » annonçait un paiement… au retrait (22/10/2026).
  const prepaye = order.payment_status
    ? order.payment_status === 'paid'
    : order.delivery_option === 'prepaid_pickup';
  lines.push({
    type: 'text',
    content: `${prepaye ? 'TOTAL PAYE' : 'TOTAL A PAYER'} = ${formatPrice(order.total_cents)}`,
    bold: true,
    fontSize: 'large',
  });
  if (order.refunded_cents && order.refunded_cents > 0) {
    lines.push({ type: 'text', content: `REMBOURSE SUR VOS FOLIES = ${formatPrice(order.refunded_cents)}`, bold: true });
  }
  if (order.balance_due_cents && order.balance_due_cents > 0) {
    lines.push({
      type: 'text',
      content: `COMPLEMENT A REGLER ${aLivrer ? 'A LA LIVRAISON' : 'AU RETRAIT'} = ${formatPrice(order.balance_due_cents)}`,
      bold: true,
    });
  }
  lines.push({
    type: 'text',
    content: prepaye
      ? 'Commande deja reglee'
      : aLivrer
        ? 'Paiement a la livraison (Bancontact ou especes)'
        : 'Paiement au retrait',
    align: 'center',
  });

  if (order.preparedBy) {
    lines.push({ type: 'text', content: normalizeForPrint(`Prepare par ${order.preparedBy}`) });
  }

  lines.push({ type: 'feed', feeds: 1 });
  lines.push({
    type: 'barcode',
    content: barcodeForOrderNumber(order.order_number),
    align: 'center',
  });
  // Le code-barres sert au comptoir ; le QR, au client : il ouvre SA commande
  // sur son téléphone (contenu, montant), sans compte ni mot de passe.
  if (order.lienClient) {
    lines.push({ type: 'feed', feeds: 1 });
    lines.push({ type: 'text', content: 'Votre commande en ligne :', align: 'center' });
    lines.push({ type: 'qrcode', content: order.lienClient, align: 'center', size: 6 });
  }
  lines.push({ type: 'feed', feeds: 1 });
  lines.push({
    type: 'text',
    content: normalizeForPrint(order.storeName || 'Les Folies Maraicheres'),
    align: 'center',
  });
  lines.push({ type: 'feed', feeds: 3 });

  return {
    lines,
    metadata: { saleNumber: order.order_number, timestamp: new Date() },
  };
}
